import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  useModal,
  useNavigateTo,
  useTireWheelPackage
} from '../../../shared/hooks/hooks';

import { PREBUILT_PACKAGE_WITHOUT_VEHICLE } from '../../../shared/constants/modals';

export default function useFitmentConfirmation(props) {
  const { fitment = {}, onAfterConfirm } = props;
  const [pendingFitmentArgs, setPendingFitmentArgs] = useState();
  const [isPendingUnselectFitment, setIsPendingUnselectFitment] =
    useState(false);
  const [isPendingSelectFitment, setIsPendingSelectFitment] = useState(false);
  const [isPendingClearRecentFitments, setIsPendingClearRecentFitments] =
    useState(false);
  const [isPendingRemoveFitment, setIsPendingRemoveFitment] = useState(false);
  const {
    confirmCancelTireWheelPackage,
    currentTireWheelPackageFlow,
    exitPackageFlow,
    incompletePackage
  } = useTireWheelPackage();

  const {
    removeFitment,
    removeRecentFitments,
    selectFitment: selectFitmentAlias,
    unselectFitment: unselectFitmentAlias
  } = fitment;

  const { closeModal, openModal } = useModal();
  const navigateTo = useNavigateTo();
  const location = useLocation();

  const unselectFitment = useCallback((...args) => {
    setPendingFitmentArgs(args);
    setIsPendingUnselectFitment(true);
  }, []);

  const selectFitment = useCallback((...args) => {
    setPendingFitmentArgs(args);
    setIsPendingSelectFitment(true);
  }, []);

  const promptRemoveFitmentConfirmation = useCallback((...args) => {
    setPendingFitmentArgs(args);
    setIsPendingRemoveFitment(true);
  }, []);

  const promptClearRecentFitmentsConfirmation = useCallback(() => {
    setIsPendingClearRecentFitments(true);
  }, []);

  function resetPendingStatus() {
    setIsPendingUnselectFitment(false);
    setIsPendingSelectFitment(false);
    setIsPendingClearRecentFitments(false);
    setIsPendingRemoveFitment(false);
    setPendingFitmentArgs();
  }

  const continueFitmentActions = useCallback(() => {
    if (isPendingUnselectFitment) {
      unselectFitmentAlias?.(...pendingFitmentArgs);
    }

    if (isPendingClearRecentFitments) {
      removeRecentFitments?.();
    }

    if (isPendingRemoveFitment) {
      removeFitment?.(...pendingFitmentArgs);
    }

    if (isPendingSelectFitment) {
      selectFitmentAlias?.(...pendingFitmentArgs);
    }
    onAfterConfirm?.();
  }, [
    isPendingUnselectFitment,
    isPendingClearRecentFitments,
    isPendingRemoveFitment,
    isPendingSelectFitment,
    removeRecentFitments,
    removeFitment,
    selectFitmentAlias,
    unselectFitmentAlias,
    pendingFitmentArgs,
    onAfterConfirm
  ]);

  const goToExitUrl = useCallback(() => {
    const hasResultsOrEditModal =
      window.location.hash === '#/fitment/vehicle/results' ||
      window.location.hash === '#/fitment/vehicle/edit';
    const exitUrl = `/${hasResultsOrEditModal ? window.location.hash : ''}`;

    setTimeout(() => {
      navigateTo(exitUrl);
      // Adding slight delay to avoid collisions with
      // hash route changes after selecting fitment
    }, 50);
  }, [navigateTo]);

  useEffect(() => {
    if (
      isPendingSelectFitment ||
      isPendingUnselectFitment ||
      isPendingClearRecentFitments ||
      isPendingRemoveFitment
    ) {
      if (
        location.pathname === '/fitmentresult/packages/prebuilt' ||
        location.pathname.startsWith('/buy-package/bundles')
      ) {
        navigateTo(location.pathname + location.search);

        openModal(PREBUILT_PACKAGE_WITHOUT_VEHICLE, {
          onClose: () => {
            resetPendingStatus();
            closeModal();
          },
          onConfirm: () => {
            closeModal();
            continueFitmentActions();
            resetPendingStatus();
            goToExitUrl();
          }
        });
      } else if (currentTireWheelPackageFlow) {
        navigateTo(location.pathname + location.search);

        confirmCancelTireWheelPackage(undefined, {
          onCancelPackage: () => {
            continueFitmentActions();
            exitPackageFlow();
            resetPendingStatus();
            goToExitUrl();
          },
          onClose: () => {
            resetPendingStatus();
          }
        });
      } else if (isPendingUnselectFitment || isPendingSelectFitment) {
        continueFitmentActions();
        resetPendingStatus();
      } else {
        resetPendingStatus();
      }
    }
  }, [
    exitPackageFlow,
    confirmCancelTireWheelPackage,
    continueFitmentActions,
    currentTireWheelPackageFlow,
    openModal,
    navigateTo,
    closeModal,
    location.pathname,
    location.search,
    isPendingUnselectFitment,
    isPendingClearRecentFitments,
    isPendingSelectFitment,
    isPendingRemoveFitment,
    goToExitUrl,
    incompletePackage
  ]);

  return {
    ...fitment,
    currentTireWheelPackageFlow,
    promptClearRecentFitmentsConfirmation,
    promptRemoveFitmentConfirmation,
    selectFitment,
    unselectFitment
  };
}
